/**
 * renderFinalImage.ts
 *
 * Offscreen Canvas 2D compositing utility for the library-mode export.
 * Draws the cropped/panned source image + text layers at the platform's
 * target resolution and returns a PNG Blob.
 *
 * Extracted from UnifiedImageEditor to keep the main component lean.
 */

import type { TextLayer } from './editorShared';
import {
  buildFont, wrapText, drawLine,
  renderEffectCanvas, getKonvaTextEffectProps,
  EFFECT_PRESETS,
  type EffectId,
} from './effects/presets';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RenderFinalImageParams {
  /** The loaded source image element (must have crossOrigin set) */
  sourceImg: HTMLImageElement;
  /** Natural dimensions of the source image */
  naturalSize: { w: number; h: number };
  /** Pan fraction 0-1 (from the position mode) */
  panFrac: { x: number; y: number };
  /** Target aspect ratio (outputWidth / outputHeight) */
  targetAR: number;
  /** Final output dimensions */
  outputWidth: number;
  outputHeight: number;
  /** Text layers in display-space coordinates */
  textLayers: TextLayer[];
  /** Current viewport dimensions (for scaling display→output) */
  viewportSize: { w: number; h: number };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Parse #rrggbb to rgba() with specified alpha */
function hexToRgba(hex: string, alpha: number): string {
  const safe = hex.startsWith('#') ? hex : '#000000';
  const full = safe.length === 4
    ? `#${safe[1]}${safe[1]}${safe[2]}${safe[2]}${safe[3]}${safe[3]}`
    : safe;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Draw a rounded rectangle (pill or rect) */
function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, radius);
  ctx.fill();
}

// ─── Main render function ────────────────────────────────────────────────────

export async function renderFinalImage(params: RenderFinalImageParams): Promise<Blob> {
  const {
    sourceImg, naturalSize, panFrac, targetAR,
    outputWidth, outputHeight, textLayers, viewportSize,
  } = params;

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext('2d')!;

  // ── 1. Draw cropped image ──────────────────────────────────────────────────

  // Compute the source rect (same math as computeScaleToFill + imageStyle)
  const imageAR = naturalSize.w / naturalSize.h;
  let w_frac: number, h_frac: number;
  if (imageAR > targetAR) {
    h_frac = 1.0;
    w_frac = targetAR / imageAR;
  } else {
    w_frac = 1.0;
    h_frac = imageAR / targetAR;
  }
  const maxPanX = Math.max(0, 1 - w_frac);
  const maxPanY = Math.max(0, 1 - h_frac);

  const srcX = panFrac.x * maxPanX * naturalSize.w;
  const srcY = panFrac.y * maxPanY * naturalSize.h;
  const srcW = w_frac * naturalSize.w;
  const srcH = h_frac * naturalSize.h;

  ctx.drawImage(sourceImg, srcX, srcY, srcW, srcH, 0, 0, outputWidth, outputHeight);

  // ── 2. Draw text layers ────────────────────────────────────────────────────

  const scaleX = outputWidth / viewportSize.w;
  const scaleY = outputHeight / viewportSize.h;

  for (const layer of textLayers) {
    if (layer.opacity <= 0) continue;

    ctx.save();
    ctx.globalAlpha = layer.opacity;

    // Output-space values
    const outX = layer.x * scaleX;
    const outY = layer.y * scaleY;
    const outW = layer.width * scaleX;
    const outFontSize = layer.fontSize * scaleX;
    const lineHeightRatio = layer.lineHeight ?? 1.3;
    const outLetterSpacing = (layer.letterSpacing ?? 0) * scaleX;
    const outBgPadding = (layer.bgPadding ?? 12) * scaleX;

    // Rotation
    if (layer.rotation !== 0) {
      ctx.translate(outX, outY);
      ctx.rotate((layer.rotation * Math.PI) / 180);
      ctx.translate(-outX, -outY);
    }

    // Apply letter spacing (modern Canvas 2D)
    if (outLetterSpacing !== 0 && 'letterSpacing' in ctx) {
      (ctx as any).letterSpacing = `${outLetterSpacing}px`;
    }

    const preset = EFFECT_PRESETS.find(p => p.id === layer.effect);

    if (preset?.isComplex) {
      // Complex effects: use the existing renderEffectCanvas at output scale
      const render = renderEffectCanvas({
        text: layer.text,
        displayWidth: outW,
        fontSize: outFontSize,
        fontFamily: layer.fontFamily,
        fontStyle: layer.fontStyle,
        fill: layer.fill,
        align: layer.align,
        effect: layer.effect,
        effectParams: layer.effectParams,
        bgImage: sourceImg,
        lineHeight: lineHeightRatio,
        letterSpacing: outLetterSpacing,
      });

      // Draw background shape behind complex effect if needed
      if (layer.bgShape !== 'none') {
        ctx.save();
        ctx.globalAlpha = layer.bgOpacity;
        ctx.fillStyle = layer.bgColor;
        // Measure actual text content width for tight fit
        const bgCtx2 = document.createElement('canvas').getContext('2d')!;
        bgCtx2.font = buildFont(layer.fontStyle, outFontSize, layer.fontFamily);
        if (outLetterSpacing !== 0 && 'letterSpacing' in bgCtx2) (bgCtx2 as any).letterSpacing = `${outLetterSpacing}px`;
        const bgLines = wrapText(bgCtx2, layer.text, outW);
        const bgLineH = outFontSize * lineHeightRatio;
        const contentH = bgLines.length * bgLineH - outFontSize * (lineHeightRatio - 1);
        const maxLineW = Math.max(...bgLines.map(l => bgCtx2.measureText(l).width), 0);
        const bgW = maxLineW + outBgPadding * 2;
        const bgH = contentH + outBgPadding * 2;
        let bgX = outX - outBgPadding;
        if (layer.align === 'center') bgX = outX + (outW - maxLineW) / 2 - outBgPadding;
        else if (layer.align === 'right') bgX = outX + outW - maxLineW - outBgPadding;
        const bgY = outY - outBgPadding;
        const radius = layer.bgShape === 'pill' ? bgH / 2 : 4 * scaleX;
        drawRoundedRect(ctx, bgX, bgY, bgW, bgH, radius);
        ctx.restore();
      }

      ctx.drawImage(
        render.canvas,
        outX - render.padX,
        outY - render.padY,
        outW + render.padX * 2,
        render.displayHeight,
      );
    } else {
      // Simple effects: draw text directly with Canvas 2D
      const font = buildFont(layer.fontStyle, outFontSize, layer.fontFamily);
      ctx.font = font;
      ctx.textBaseline = 'top';

      const lines = wrapText(ctx, layer.text, outW);
      const lineH = outFontSize * lineHeightRatio;

      // Draw background shape — tightly wraps actual text content
      if (layer.bgShape !== 'none') {
        ctx.save();
        ctx.globalAlpha = layer.bgOpacity;
        ctx.fillStyle = layer.bgColor;
        const contentH = lines.length * lineH - outFontSize * (lineHeightRatio - 1);
        const maxLineW = Math.max(...lines.map(l => ctx.measureText(l).width), 0);
        const bgW = maxLineW + outBgPadding * 2;
        const bgH = contentH + outBgPadding * 2;
        let bgX = outX - outBgPadding;
        if (layer.align === 'center') bgX = outX + (outW - maxLineW) / 2 - outBgPadding;
        else if (layer.align === 'right') bgX = outX + outW - maxLineW - outBgPadding;
        const bgY = outY - outBgPadding;
        const radius = layer.bgShape === 'pill' ? bgH / 2 : 4 * scaleX;
        drawRoundedRect(ctx, bgX, bgY, bgW, bgH, radius);
        ctx.restore();
        // Restore text opacity
        ctx.globalAlpha = layer.opacity;
      }

      // Apply simple effect props (gradient_fill, chrome_stroke)
      const effectProps = getKonvaTextEffectProps(
        layer.effect, layer.effectParams, outFontSize,
      );

      // Chrome stroke effect — draw stroke underneath
      if (layer.effect === 'chrome_stroke') {
        const strokeColor = effectProps.stroke as string || '#c0c0c0';
        const strokeWidth = effectProps.strokeWidth as number || 3;
        const shadowOffsetX = effectProps.shadowOffsetX as number || 3;
        const shadowOffsetY = effectProps.shadowOffsetY as number || 3;
        const shadowBlur = effectProps.shadowBlur as number || 6;

        ctx.save();
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth * scaleX;
        ctx.shadowColor = 'rgba(0,0,0,0.7)';
        ctx.shadowOffsetX = shadowOffsetX * scaleX;
        ctx.shadowOffsetY = shadowOffsetY * scaleX;
        ctx.shadowBlur = shadowBlur * scaleX;

        for (let i = 0; i < lines.length; i++) {
          const ly = outY + i * lineH;
          const lx = alignX(lines[i], ctx, outX, outW, layer.align);
          ctx.strokeText(lines[i], lx, ly);
        }
        ctx.restore();
      }

      // Fill text
      if (layer.effect === 'gradient_fill') {
        const stops = effectProps.fillLinearGradientColorStops as (string | number)[];
        if (stops) {
          const grad = ctx.createLinearGradient(0, outY, 0, outY + outFontSize * lineHeightRatio);
          for (let i = 0; i < stops.length; i += 2) {
            grad.addColorStop(stops[i] as number, stops[i + 1] as string);
          }
          ctx.fillStyle = grad;
        } else {
          ctx.fillStyle = layer.fill;
        }
      } else {
        ctx.fillStyle = layer.fill;
      }

      for (let i = 0; i < lines.length; i++) {
        const ly = outY + i * lineH;
        const lx = alignX(lines[i], ctx, outX, outW, layer.align);
        ctx.fillText(lines[i], lx, ly);
      }
    }

    // Reset letter spacing
    if ('letterSpacing' in ctx) {
      (ctx as any).letterSpacing = '0px';
    }

    ctx.restore();
  }

  // ── 3. Export as PNG blob ──────────────────────────────────────────────────

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob returned null'));
    }, 'image/png');
  });
}

// ─── Alignment helper ────────────────────────────────────────────────────────

function alignX(
  line: string,
  ctx: CanvasRenderingContext2D,
  originX: number,
  areaWidth: number,
  align: 'left' | 'center' | 'right',
): number {
  const lineW = ctx.measureText(line).width;
  if (align === 'center') return originX + (areaWidth - lineW) / 2;
  if (align === 'right') return originX + areaWidth - lineW;
  return originX;
}
